import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const formatPrice = (p) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(p || 0);

const compressImage = (file, maxWidth = 600) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, "") + ".jpg", {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              reject(new Error('Canvas to Blob conversion failed'));
            }
          },
          'image/jpeg',
          0.8
        );
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
};

const normalizePhone = (val) => {
  if (!val) return '';
  const digits = String(val).replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('57')) return digits;
  return `57${digits}`;
};

const getColombiaToday = () => {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Bogota',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(new Date());
};

const calculateAutoProductionDate = (deliveryDateVal, isRefrigerated) => {
  if (!deliveryDateVal || isRefrigerated) return '';
  const todayStr = getColombiaToday();
  if (deliveryDateVal <= todayStr) {
    return todayStr;
  }
  const todayDate = new Date(`${todayStr}T12:00:00`);
  const delivDate = new Date(`${deliveryDateVal}T12:00:00`);
  const diffDays = Math.round((delivDate - todayDate) / (1000 * 60 * 60 * 24));
  
  if (diffDays <= 0) {
    return todayStr;
  } else if (diffDays === 1) {
    return deliveryDateVal;
  } else {
    delivDate.setDate(delivDate.getDate() - 1);
    const prodStr = delivDate.toISOString().split('T')[0];
    return prodStr < todayStr ? todayStr : prodStr;
  }
};

export default function OrderCreateEditDialog({
  open,
  onOpenChange,
  editingOrder,
  stores = [],
  products = [],
  onOrderSaved,
  API,
  getHeaders
}) {
  const [form, setForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    delivery_type: 'pickup',
    store_id: '',
    delivery_address: '',
    delivery_date: '',
    production_date: '',
    initial_payment: 0,
    payment_method: 'efectivo',
    observations: '',
    delivery_fee: 0,
    delivery_time_slot: '',
    payment_date: '',
  });

  const [orderItems, setOrderItems] = useState([]);

  // Subform item states
  const [itemType, setItemType] = useState('existing'); // 'existing' | 'custom'
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all');
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedSizeName, setSelectedSizeName] = useState('');
  const [selectedFlavor, setSelectedFlavor] = useState('');
  const [selectedFilling, setSelectedFilling] = useState('');
  const [additionalPrice, setAdditionalPrice] = useState(0);
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [itemObservations, setItemObservations] = useState('');

  // Custom cake states
  const [customName, setCustomName] = useState('Torta Personalizada');
  const [customPrice, setCustomPrice] = useState(150000);
  const [customSize, setCustomSize] = useState('15 porciones');
  const [customFilling, setCustomFilling] = useState('Arequipe');
  const [customFlavor, setCustomFlavor] = useState('Vainilla');
  const [customImages, setCustomImages] = useState([]);
  const [customObservations, setCustomObservations] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [saving, setSaving] = useState(false);

  // Initialize or reset form when dialog opens or editingOrder changes
  useEffect(() => {
    if (!open) return;

    if (editingOrder) {
      setForm({
        customer_name: editingOrder.customer_name || '',
        customer_email: editingOrder.customer_email || '',
        customer_phone: editingOrder.customer_phone || '',
        delivery_type: editingOrder.delivery_type || 'pickup',
        store_id: editingOrder.store_id || (stores.length > 0 ? stores[0].id : ''),
        delivery_address: editingOrder.delivery_address || '',
        delivery_date: editingOrder.delivery_date ? editingOrder.delivery_date.split('T')[0] : '',
        production_date: editingOrder.production_date ? editingOrder.production_date.split('T')[0] : '',
        initial_payment: editingOrder.amount_paid || 0,
        payment_method: editingOrder.payment_method || 'efectivo',
        observations: editingOrder.observations || '',
        delivery_fee: editingOrder.delivery_fee || 0,
        delivery_time_slot: editingOrder.delivery_time_slot || '',
        payment_date: editingOrder.payments && editingOrder.payments.length > 0 && editingOrder.payments[0].date
          ? editingOrder.payments[0].date.split('T')[0]
          : '',
      });
      setOrderItems(editingOrder.items || []);
    } else {
      setForm({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        delivery_type: 'pickup',
        store_id: stores.length > 0 ? stores[0].id : '',
        delivery_address: '',
        delivery_date: '',
        production_date: '',
        initial_payment: 0,
        payment_method: 'efectivo',
        observations: '',
        delivery_fee: 0,
        delivery_time_slot: '',
        payment_date: '',
      });
      setOrderItems([]);
    }

    setSelectedProductId('');
    setSelectedSizeName('');
    setCustomImages([]);
    setSelectedCategoryFilter('all');
  }, [open, editingOrder, stores]);

  // Check if cart has torta to restrict pickup time slots
  const hasTorta = useMemo(() => {
    return orderItems.some(item => {
      if (item.productId === 'custom') return true;
      const prod = products.find(p => p.id === item.productId);
      const cat = prod?.category || item.category || '';
      return ['refrigeradas', 'clasicas', 'saludables', 'tortas', 'tortas-especiales', 'tortas-personalizadas', 'tortas-tematicas'].includes(cat);
    });
  }, [orderItems, products]);

  // Check if all items in order are refrigerated
  const isOnlyRefrigerated = useMemo(() => {
    if (orderItems.length === 0) return false;
    return orderItems.every(item => {
      const prod = products.find(p => p.id === item.productId);
      const cat = (prod?.category || item.category || '').toLowerCase();
      return cat === 'refrigeradas' || cat === 'refrigeradas-premium';
    });
  }, [orderItems, products]);

  // When all items are refrigerated, do not set automatic production date
  useEffect(() => {
    if (isOnlyRefrigerated && !editingOrder) {
      setForm(prev => ({ ...prev, production_date: '' }));
    }
  }, [isOnlyRefrigerated, editingOrder]);

  // Requirement 2: Check if order items contain Chocoarequipe from clasicas
  const hasChocoarequipeClasica = useMemo(() => {
    return orderItems.some(item => {
      const prod = products.find(p => p.id === item.productId);
      const name = (item.name || prod?.name || '').toLowerCase();
      const cat = (item.category || prod?.category || '').toLowerCase();
      return name.includes('chocoarequipe') && cat === 'clasicas';
    });
  }, [orderItems, products]);

  // Enforce Sede Boston if order contains Chocoarequipe clasica
  useEffect(() => {
    if (hasChocoarequipeClasica && form.delivery_type === 'pickup' && stores.length > 0) {
      const bostonStore = stores.find(s => (s.name || '').toLowerCase().includes('boston'));
      if (bostonStore && form.store_id !== bostonStore.id) {
        setForm(prev => ({ ...prev, store_id: bostonStore.id }));
      }
    }
  }, [hasChocoarequipeClasica, form.delivery_type, form.store_id, stores]);

  useEffect(() => {
    if (form.delivery_type === 'pickup' && hasTorta) {
      if (form.delivery_time_slot && !['2:00 PM', '3:00 PM - 6:00 PM'].includes(form.delivery_time_slot)) {
        setForm(prev => ({ ...prev, delivery_time_slot: '' }));
      }
    }
  }, [form.delivery_type, hasTorta, form.delivery_time_slot]);

  // Filtered products for subform
  const filteredProducts = useMemo(() => {
    if (selectedCategoryFilter === 'all') return products;
    return products.filter(p => p.category === selectedCategoryFilter);
  }, [products, selectedCategoryFilter]);

  const selectedProduct = useMemo(() => {
    return products.find(p => p.id === selectedProductId);
  }, [products, selectedProductId]);

  useEffect(() => {
    if (selectedProduct) {
      if (selectedProduct.sizes?.length > 0) {
        setSelectedSizeName(selectedProduct.sizes[0].name);
      } else {
        setSelectedSizeName('');
      }

      let defaultFlavor = '';
      if (['refrigeradas', 'clasicas', 'saludables'].includes(selectedProduct.category)) {
        defaultFlavor = 'Predeterminado';
      } else if (selectedProduct.name.toLowerCase().includes('vasca')) {
        defaultFlavor = 'Frutos Rojos';
      } else if (selectedProduct.flavors?.length > 0) {
        defaultFlavor = selectedProduct.flavors[0].name;
      } else {
        defaultFlavor = 'Vainilla';
      }

      let defaultFilling = '';
      if (['refrigeradas', 'clasicas', 'saludables'].includes(selectedProduct.category)) {
        defaultFilling = 'Predeterminado';
      } else if (selectedProduct.fillings?.length > 0) {
        defaultFilling = selectedProduct.fillings[0].name;
      } else {
        defaultFilling = 'Arequipe';
      }

      setSelectedFlavor(defaultFlavor);
      setSelectedFilling(defaultFilling);
    } else {
      setSelectedSizeName('');
      setSelectedFlavor('');
      setSelectedFilling('');
    }
  }, [selectedProductId, selectedProduct]);

  // Image Upload for Custom Cakes
  const handleCustomImageUpload = async (e) => {
    if (customImages.length >= 3) {
      toast.error('Puedes subir un máximo de 3 imágenes de referencia');
      return;
    }
    const file = e.target.files[0];
    if (!file) return;
    setUploadingImage(true);
    const toastId = toast.loading('Subiendo imagen de referencia...');
    try {
      let fileToUpload = file;
      if (file.type.startsWith('image/')) {
        try {
          fileToUpload = await compressImage(file, 600);
        } catch (compressErr) {
          console.error(compressErr);
        }
      }
      const formData = new FormData();
      formData.append('file', fileToUpload);
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API}/admin/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });
      setCustomImages(prev => [...prev, data.url].slice(0, 3));
      toast.success('Imagen de referencia subida', { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error('Error al subir imagen', { id: toastId });
    } finally {
      setUploadingImage(false);
    }
  };

  // Add Item to Order
  const handleAddItem = () => {
    if (itemType === 'existing') {
      const prod = products.find(p => p.id === selectedProductId);
      if (!prod) {
        toast.error('Selecciona un producto del catálogo');
        return;
      }

      const sizeObj = prod.sizes?.find(s => s.name === selectedSizeName) || (prod.sizes?.length ? prod.sizes[0] : null);
      const basePrice = sizeObj ? (sizeObj.price ?? prod.base_price) : prod.base_price;
      const price = basePrice + (Number(additionalPrice) || 0);

      const newItem = {
        productId: prod.id,
        name: prod.name,
        category: prod.category,
        size: sizeObj ? sizeObj.name : 'Estándar',
        filling: prod.category === 'pinateria' ? '' : (selectedFilling || (prod.fillings?.length ? prod.fillings[0].name : '')),
        flavor: prod.category === 'pinateria' ? '' : (selectedFlavor || (prod.flavors?.length ? prod.flavors[0].name : '')),
        image: prod.image,
        complements: [],
        observations: itemObservations,
        subtotal: price,
        quantity: selectedQuantity,
        base_price: price,
        filling_price: 0,
        flavor_price: 0,
      };

      setOrderItems(prev => [...prev, newItem]);
      setSelectedProductId('');
      setAdditionalPrice(0);
      setSelectedQuantity(1);
      setItemObservations('');
      toast.success('Producto agregado al pedido');
    } else {
      if (!customName.trim()) {
        toast.error('Especifica el nombre del ítem');
        return;
      }
      const newItem = {
        productId: 'custom',
        name: customName,
        size: customSize,
        filling: customFilling,
        flavor: customFlavor,
        category: 'tortas-personalizadas',
        image: customImages.join(','),
        complements: [],
        observations: customObservations || 'Pedido Personalizado creado desde Admin',
        subtotal: Number(customPrice),
        quantity: selectedQuantity
      };

      setOrderItems(prev => [...prev, newItem]);
      setCustomName('Torta Personalizada');
      setCustomPrice(150000);
      setCustomImages([]);
      setCustomObservations('');
      setSelectedQuantity(1);
      toast.success('Producto personalizado agregado');
    }
  };

  // Submit Order Creation / Update
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_name.trim()) {
      toast.error('El Nombre del Cliente es obligatorio');
      return;
    }
    const cleanPhone = normalizePhone(form.customer_phone);
    if (!cleanPhone || cleanPhone.length < 10) {
      toast.error('El Teléfono del Cliente es obligatorio y debe tener mínimo 10 dígitos');
      return;
    }
    if (!form.store_id) {
      toast.error('La Sede es obligatoria');
      return;
    }
    if (!form.delivery_date) {
      toast.error('La Fecha de Entrega es obligatoria');
      return;
    }
    if (!form.production_date && !isOnlyRefrigerated) {
      toast.error('La Fecha de Producción es obligatoria');
      return;
    }
    if (orderItems.length === 0) {
      toast.error('Debes agregar al menos un producto al pedido');
      return;
    }

    const store = stores.find(s => s.id === form.store_id) || (stores.length > 0 ? stores[0] : null);

    // Requirement 2: Strict check on Chocoarequipe clasica
    if (hasChocoarequipeClasica && form.delivery_type === 'pickup') {
      const isBoston = (store?.name || '').toLowerCase().includes('boston');
      if (!isBoston) {
        toast.error('La Torta Chocoarequipe clásica solo puede ser reclamada en Sede Boston');
        return;
      }
    }

    const finalEmail = form.customer_email && form.customer_email.trim()
      ? form.customer_email.trim()
      : 'pedidos@keiksreposteria.com';

    setSaving(true);
    try {
      const payload = {
        customer_name: form.customer_name,
        customer_email: finalEmail,
        customer_phone: cleanPhone,
        items: orderItems,
        delivery_type: form.delivery_type,
        store_id: store ? store.id : '',
        store_name: store ? store.name : '',
        delivery_address: form.delivery_type === 'delivery' ? form.delivery_address : '',
        delivery_date: form.delivery_date,
        production_date: form.production_date,
        initial_payment: Number(form.initial_payment) || 0,
        payment_method: form.payment_method,
        payment_date: form.payment_date || new Date().toISOString().split('T')[0],
        observations: form.observations,
        delivery_fee: form.delivery_type === 'delivery' ? (Number(form.delivery_fee) || 0) : 0.0,
        delivery_time_slot: form.delivery_time_slot,
        created_by_admin: true,
      };

      if (editingOrder) {
        await axios.put(`${API}/admin/orders/${editingOrder.id}`, payload, { withCredentials: true, headers: getHeaders() });
        toast.success('Pedido actualizado con éxito');
      } else {
        await axios.post(`${API}/orders`, payload, { withCredentials: true, headers: getHeaders() });
        toast.success('Pedido creado con éxito');
      }

      onOpenChange(false);
      if (onOrderSaved) onOrderSaved();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Error al guardar el pedido');
    } finally {
      setSaving(false);
    }
  };

  const dialogItemsTotal = useMemo(() => {
    return orderItems.reduce((acc, item) => acc + (item.subtotal * (item.quantity || 1)), 0);
  }, [orderItems]);

  const dialogDeliveryFee = form.delivery_type === 'delivery' ? (Number(form.delivery_fee) || 0) : 0;
  const dialogTotalPedido = dialogItemsTotal + dialogDeliveryFee;
  const dialogSaldoRestante = Math.max(0, dialogTotalPedido - (Number(form.initial_payment) || 0));

  // Stores available for selection (restricted to Boston if Chocoarequipe clasica)
  const selectableStores = useMemo(() => {
    if (hasChocoarequipeClasica && form.delivery_type === 'pickup') {
      return stores.filter(s => (s.name || '').toLowerCase().includes('boston'));
    }
    return stores;
  }, [stores, hasChocoarequipeClasica, form.delivery_type]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[95vw] bg-[#fcfbf9] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-[#46371c]">
            {editingOrder ? 'Editar Pedido' : 'Crear Nuevo Pedido'}
          </DialogTitle>
          <DialogDescription className="font-body text-sm text-[#75664d]">
            {editingOrder ? 'Modifica la información y productos del pedido' : 'Registra manualmente un pedido con múltiples ítems y fotos'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Columna Izquierda: Información del Cliente, Entrega, Producción y Observaciones */}
            <div className="space-y-4">
              
              {/* Datos del Cliente */}
              <div className="bg-[#fcfbf9] p-4 border border-[#e5dfd3] rounded-2xl space-y-3">
                <h4 className="font-heading text-xs font-bold text-[#46371c] border-b border-[#e5dfd3]/60 pb-1.5">Información del Cliente</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="font-body text-xs text-[#75664d] mb-1 block font-medium">Nombre del Cliente *</label>
                    <input
                      required
                      value={form.customer_name}
                      onChange={e => setForm(prev => ({ ...prev, customer_name: e.target.value }))}
                      className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                      data-testid="admin-order-customer-name"
                    />
                  </div>
                  <div>
                    <label className="font-body text-xs text-[#75664d] mb-1 block font-medium">Teléfono (WhatsApp) *</label>
                    <input
                      required
                      value={form.customer_phone}
                      onChange={e => {
                        const cleaned = e.target.value.replace(/\D/g, '');
                        setForm(prev => ({ ...prev, customer_phone: cleaned }));
                      }}
                      onBlur={() => {
                        if (form.customer_phone) {
                          setForm(prev => ({ ...prev, customer_phone: normalizePhone(prev.customer_phone) }));
                        }
                      }}
                      placeholder="Ej: 3001234567"
                      className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                      data-testid="admin-order-customer-phone"
                    />
                    <span className="text-[10px] text-[#75664d]">Solo números. Agrega 57 automáticamente.</span>
                  </div>
                </div>
                <div>
                  <label className="font-body text-xs text-[#75664d] mb-1 block font-medium">Email (Opcional)</label>
                  <input
                    type="email"
                    value={form.customer_email}
                    onChange={e => setForm(prev => ({ ...prev, customer_email: e.target.value }))}
                    className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                    data-testid="admin-order-customer-email"
                  />
                </div>
              </div>

              {/* Detalles de Entrega */}
              <div className="bg-[#fcfbf9] p-4 border border-[#e5dfd3] rounded-2xl space-y-3">
                <h4 className="font-heading text-xs font-bold text-[#46371c] border-b border-[#e5dfd3]/60 pb-1.5">Detalles de Entrega</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-body text-xs text-[#75664d] mb-1 block font-medium">Tipo de Entrega</label>
                    <Select
                      value={form.delivery_type}
                      onValueChange={v => setForm(prev => ({ ...prev, delivery_type: v }))}
                    >
                      <SelectTrigger className="w-full rounded-xl border-[#e5dfd3]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pickup">Recoger en Tienda</SelectItem>
                        <SelectItem value="delivery">Envío a Domicilio</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="font-body text-xs text-[#75664d] mb-1 block font-medium">Sede *</label>
                    <Select
                      value={form.store_id}
                      onValueChange={v => setForm(prev => ({ ...prev, store_id: v }))}
                    >
                      <SelectTrigger className="w-full rounded-xl border-[#e5dfd3]">
                        <SelectValue placeholder="Selecciona sede..." />
                      </SelectTrigger>
                      <SelectContent>
                        {selectableStores.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Sede Boston restriction alert */}
                {hasChocoarequipeClasica && form.delivery_type === 'pickup' && (
                  <div className="bg-amber-50 border border-amber-300 rounded-xl p-2.5 text-xs text-amber-900 font-medium leading-relaxed">
                    ⚠️ <strong>Restricción de Sede:</strong> El pedido incluye la <strong>Torta Chocoarequipe (Clásicas)</strong>. Por lineamiento de producción, únicamente se puede reclamar en <strong>Sede Boston</strong>.
                  </div>
                )}

                {form.delivery_type === 'delivery' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="font-body text-xs text-[#75664d] mb-1 block font-medium">Dirección de Entrega (Incluir Barrio) *</label>
                      <input
                        required
                        value={form.delivery_address}
                        onChange={e => setForm(prev => ({ ...prev, delivery_address: e.target.value }))}
                        className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                        data-testid="admin-order-address"
                      />
                    </div>
                    <div>
                      <label className="font-body text-xs text-[#75664d] mb-1 block font-medium">Valor del Domicilio *</label>
                      <input
                        type="number"
                        required
                        value={form.delivery_fee}
                        onChange={e => setForm(prev => ({ ...prev, delivery_fee: Number(e.target.value) }))}
                        className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                      />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="font-body text-xs text-[#75664d] mb-1 block font-medium">Fecha de Entrega</label>
                    <input
                      type="date"
                      value={form.delivery_date}
                      onChange={e => {
                        const val = e.target.value;
                        setForm(prev => {
                          let prodDate = prev.production_date;
                          if (!isOnlyRefrigerated) {
                            prodDate = calculateAutoProductionDate(val, false);
                          }
                          return { ...prev, delivery_date: val, production_date: prodDate };
                        });
                      }}
                      className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                    />
                  </div>
                  <div>
                    <label className="font-body text-xs text-[#75664d] mb-1 block font-medium">Horario de Entrega</label>
                    <Select
                      value={form.delivery_time_slot}
                      onValueChange={v => setForm(prev => ({ ...prev, delivery_time_slot: v }))}
                    >
                      <SelectTrigger className="w-full h-10 rounded-xl border-[#e5dfd3] text-sm bg-white">
                        <SelectValue placeholder="Selecciona un horario..." />
                      </SelectTrigger>
                      <SelectContent>
                        {form.delivery_type === 'pickup' ? (
                          <>
                            {!hasTorta && <SelectItem value="9:00 AM - 12:00 M">9:00 AM - 12:00 M</SelectItem>}
                            {!hasTorta && <SelectItem value="12:00 M - 3:00 PM">12:00 M - 3:00 PM</SelectItem>}
                            <SelectItem value="2:00 PM">2:00 PM</SelectItem>
                            <SelectItem value="3:00 PM - 6:00 PM">3:00 PM - 6:00 PM</SelectItem>
                          </>
                        ) : (
                          <>
                            <SelectItem value="9:00 AM - 12:00 M">9:00 AM - 12:00 M (9 a 12)</SelectItem>
                            <SelectItem value="12:00 M - 3:00 PM">12:00 M - 3:00 PM (12 a 3)</SelectItem>
                            <SelectItem value="3:00 PM - 6:00 PM">3:00 PM - 6:00 PM (3 a 6)</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Producción y Pago */}
              <div className="bg-[#fcfbf9] p-4 border border-[#e5dfd3] rounded-2xl space-y-3">
                <h4 className="font-heading text-xs font-bold text-[#46371c] border-b border-[#e5dfd3]/60 pb-1.5">Producción y Pago</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="font-body text-xs text-[#75664d] mb-1 block font-medium">Forma de Pago</label>
                    <Select
                      value={form.payment_method}
                      onValueChange={v => setForm(prev => ({ ...prev, payment_method: v }))}
                    >
                      <SelectTrigger className="w-full h-10 rounded-xl border-[#e5dfd3] text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="credito">Crédito</SelectItem>
                        <SelectItem value="trans_keiks">Trans Keiks</SelectItem>
                        <SelectItem value="trans_esteba">Trans Esteba</SelectItem>
                        <SelectItem value="datafono">Datáfono</SelectItem>
                        <SelectItem value="online">Online / Pasarela</SelectItem>
                        <SelectItem value="efectivo">Efectivo</SelectItem>
                        <SelectItem value="rappi">Rappi</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="font-body text-xs text-[#75664d] mb-1 block font-medium">Abono Inicial (COP)</label>
                    <input
                      type="number"
                      value={form.initial_payment}
                      onChange={e => setForm(prev => ({ ...prev, initial_payment: Number(e.target.value) }))}
                      className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                    />
                  </div>
                  <div>
                    <label className="font-body text-xs text-[#75664d] mb-1 block font-medium">Fecha de Pago</label>
                    <input
                      type="date"
                      value={form.payment_date}
                      onChange={e => setForm(prev => ({ ...prev, payment_date: e.target.value }))}
                      className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                    />
                  </div>
                  <div>
                    <label className="font-body text-xs text-[#75664d] mb-1 block font-medium">
                      Fecha de Producción {isOnlyRefrigerated ? <span className="text-blue-600 font-normal">(Opcional para refrigeradas)</span> : <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="date"
                      value={form.production_date}
                      onChange={e => setForm(prev => ({ ...prev, production_date: e.target.value }))}
                      className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                    />
                  </div>
                </div>
              </div>

              {/* Observaciones generales */}
              <div>
                <label className="font-body text-xs text-[#75664d] mb-1 block font-medium">Observaciones Generales</label>
                <textarea
                  value={form.observations}
                  onChange={e => setForm(prev => ({ ...prev, observations: e.target.value }))}
                  rows={2}
                  className="w-full p-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba] resize-none bg-white"
                />
              </div>

            </div>

            {/* Columna Derecha: Ítems agregados, Subformulario de adición y Totales */}
            <div className="space-y-4">
              
              {/* List of current items added */}
              <div className="bg-[#fcfbf9] p-4 border border-[#e5dfd3] rounded-2xl">
                <h4 className="font-heading text-xs font-bold text-[#46371c] border-b border-[#e5dfd3]/60 pb-1.5 block">Ítems del Pedido ({orderItems.length})</h4>
                {orderItems.length > 0 ? (
                  <div className="space-y-2 max-h-[22vh] overflow-y-auto pr-1">
                    {orderItems.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2.5 bg-[#f4f1ea] rounded-xl text-xs font-body border border-[#e5dfd3]">
                        <div className="flex items-center gap-2">
                          {item.image ? (
                            <img src={item.image.split(',')[0]} alt="" className="w-8 h-8 rounded object-cover border border-[#e5dfd3]" />
                          ) : (
                            <div className="w-8 h-8 rounded bg-[#e5dfd3] flex items-center justify-center font-heading text-[10px]">T</div>
                          )}
                          <div>
                            <p className="font-semibold text-[#46371c]">{item.name}</p>
                            <p className="text-[#75664d] text-[10px]">{item.size} {item.flavor && `| Sabor: ${item.flavor}`} {item.filling && `| Relleno: ${item.filling}`}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-semibold">{formatPrice(item.subtotal * (item.quantity || 1))}</span>
                          <button type="button" onClick={() => setOrderItems(prev => prev.filter((_, i) => i !== idx))} className="text-red-500 hover:text-red-700 font-semibold">
                            Eliminar
                          </button>
                        </div>
                      </div>
                    ))}
                    <div className="text-right font-body text-xs text-[#46371c] font-semibold pr-2 pt-1">
                      Subtotal Pedido: {formatPrice(dialogItemsTotal)}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4 bg-[#f4f1ea]/30 rounded-xl text-xs font-body text-[#75664d] border border-dashed border-[#e5dfd3]">
                    No has agregado ningún ítem al pedido todavía.
                  </div>
                )}
              </div>

              {/* Subform to configure and add a product */}
              <div className="p-4 bg-[#f4f1ea]/40 border border-[#e5dfd3] rounded-2xl space-y-3">
                <div className="flex justify-between items-center pb-2 border-b border-[#e5dfd3]/60">
                  <span className="font-heading text-xs font-bold text-[#46371c]">Agregar Producto al Pedido</span>
                  <div className="flex gap-3">
                    <label className="flex items-center gap-1.5 font-body text-[10px] text-[#46371c] cursor-pointer">
                      <input
                        type="radio"
                        checked={itemType === 'existing'}
                        onChange={() => setItemType('existing')}
                        className="w-3 h-3"
                      />
                      Catálogo
                    </label>
                    <label className="flex items-center gap-1.5 font-body text-[10px] text-[#46371c] cursor-pointer">
                      <input
                        type="radio"
                        checked={itemType === 'custom'}
                        onChange={() => setItemType('custom')}
                        className="w-3 h-3"
                      />
                      Personalizado
                    </label>
                  </div>
                </div>

                {itemType === 'existing' ? (
                  <div className="space-y-3">
                    <div>
                      <label className="font-body text-[10px] text-[#75664d] mb-1.5 block font-medium">Categorías</label>
                      <div className="flex gap-1 overflow-x-auto pb-1 scrollbar-thin scrollbar-thumb-gray-200">
                        {[
                          { id: 'all', name: 'Todas' },
                          { id: 'tortas-especiales', name: 'Especiales' },
                          { id: 'tortas-personalizadas', name: 'Personalizadas' },
                          { id: 'tortas-tematicas', name: 'Temáticas' },
                          { id: 'mesa-dulces', name: 'Mesa Dulces' },
                          { id: 'detalles-regalos', name: 'Detalles' },
                          { id: 'tortas', name: 'Tortas' },
                          { id: 'refrigeradas', name: 'Refrigeradas' },
                          { id: 'clasicas', name: 'Clásicas' },
                          { id: 'saludables', name: 'Saludables' },
                          { id: 'grados', name: 'Grados' },
                          { id: 'comunion', name: 'Comunión' },
                          { id: 'pinateria', name: 'Piñatería' },
                          { id: 'panaderia', name: 'Panadería' },
                          { id: 'bebidas', name: 'Bebidas' },
                          { id: 'cafeteria', name: 'Cafetería' }
                        ].map(cat => (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => setSelectedCategoryFilter(cat.id)}
                            className={`px-2.5 py-1 rounded-full text-[10px] font-body font-medium transition-colors whitespace-nowrap ${
                              selectedCategoryFilter === cat.id
                                ? 'bg-[#46371c] text-white'
                                : 'bg-[#f4f1ea] text-[#75664d] hover:bg-[#e5dfd3]'
                            }`}
                          >
                            {cat.name}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="font-body text-[10px] text-[#75664d] mb-1 block font-medium">Seleccionar Producto (Haz clic en la foto)</label>
                      <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto p-1 bg-white rounded-xl border border-[#e5dfd3]">
                        {filteredProducts.map(p => (
                          <Card
                            key={p.id}
                            onClick={() => setSelectedProductId(p.id)}
                            className={`cursor-pointer overflow-hidden border transition-all rounded-lg text-center flex flex-col p-1.5 ${
                              selectedProductId === p.id
                                ? 'border-[#a0d5ba] bg-[#eef6f2] ring-1 ring-[#a0d5ba]'
                                : 'border-[#e5dfd3]/60 hover:border-[#a0d5ba]/40'
                            }`}
                          >
                            <div className="aspect-video w-full rounded-md overflow-hidden bg-gray-100">
                              <img src={p.image} alt="" className="w-full h-full object-cover" />
                            </div>
                            <span className="font-body text-[9px] font-semibold text-[#46371c] truncate mt-1 block">
                              {p.name}
                            </span>
                            <span className="font-body text-[8px] text-[#75664d]">
                              {formatPrice(p.base_price)}
                            </span>
                          </Card>
                        ))}
                      </div>
                    </div>

                    {selectedProduct && selectedProduct.sizes && selectedProduct.sizes.length > 0 && (
                      <div>
                        <label className="font-body text-[10px] text-[#75664d] mb-0.5 block font-medium">Seleccionar Tamaño</label>
                        <Select
                          value={selectedSizeName}
                          onValueChange={setSelectedSizeName}
                        >
                          <SelectTrigger className="w-full h-8 rounded-lg border-[#e5dfd3] text-xs">
                            <SelectValue placeholder="Selecciona el tamaño..." />
                          </SelectTrigger>
                          <SelectContent>
                            {selectedProduct.sizes.map(s => (
                              <SelectItem key={s.name} value={s.name}>
                                {s.name} ({formatPrice(s.price ?? selectedProduct.base_price)})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {selectedProduct && (<>
                      {selectedProduct.category !== 'pinateria' && (
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <div>
                            <label className="font-body text-[10px] text-[#75664d] mb-0.5 block font-medium">Sabor</label>
                            {selectedProduct.name.toLowerCase().includes('vasca') ? (
                              <Select
                                value={selectedFlavor}
                                onValueChange={setSelectedFlavor}
                              >
                                <SelectTrigger className="w-full h-8 rounded-lg border-[#e5dfd3] text-xs bg-white">
                                  <SelectValue placeholder="Seleccionar sabor..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Frutos Rojos">Frutos Rojos</SelectItem>
                                  <SelectItem value="Arequipe">Arequipe</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <input
                                value={selectedFlavor}
                                onChange={e => setSelectedFlavor(e.target.value)}
                                placeholder="Sabor..."
                                disabled={selectedProduct && !['tortas-especiales', 'grados', 'comunion'].includes(selectedProduct.category)}
                                className="w-full h-8 px-2 rounded-lg border border-[#e5dfd3] font-body text-xs text-[#46371c] disabled:bg-gray-100 disabled:cursor-not-allowed"
                              />
                            )}
                          </div>
                          <div>
                            <label className="font-body text-[10px] text-[#75664d] mb-0.5 block font-medium">Relleno</label>
                            <input
                              value={selectedFilling}
                              onChange={e => setSelectedFilling(e.target.value)}
                              placeholder="Relleno..."
                              disabled={selectedProduct && ['refrigeradas', 'saludables', 'clasicas'].includes(selectedProduct.category)}
                              className="w-full h-8 px-2 rounded-lg border border-[#e5dfd3] font-body text-xs text-[#46371c] disabled:bg-gray-100 disabled:cursor-not-allowed"
                            />
                          </div>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <div>
                          <label className="font-body text-[10px] text-[#75664d] mb-0.5 block font-medium">Precio Adicional</label>
                          <input
                            type="number"
                            value={additionalPrice}
                            onChange={e => setAdditionalPrice(Number(e.target.value))}
                            className="w-full h-8 px-2 rounded-lg border border-[#e5dfd3] font-body text-xs text-[#46371c]"
                          />
                        </div>
                        <div>
                          <label className="font-body text-[10px] text-[#75664d] mb-0.5 block font-medium">Cantidad</label>
                          <Select
                            value={String(selectedQuantity)}
                            onValueChange={v => setSelectedQuantity(Number(v))}
                          >
                            <SelectTrigger className="w-full h-8 rounded-lg border-[#e5dfd3] text-xs bg-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(q => (
                                <SelectItem key={q} value={String(q)}>{q}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="mt-2">
                        <label className="font-body text-[10px] text-[#75664d] mb-0.5 block font-medium">Observación del Producto / Torta</label>
                        <input
                          value={itemObservations}
                          onChange={e => setItemObservations(e.target.value)}
                          placeholder="Ej: Escribir Feliz Cumpleaños, sin azúcar, etc."
                          className="w-full h-8 px-2 rounded-lg border border-[#e5dfd3] font-body text-xs text-[#46371c]"
                        />
                      </div>
                    </>)}
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="font-body text-[10px] text-[#75664d] mb-0.5 block">Nombre del Ítem *</label>
                        <input
                          value={customName}
                          onChange={e => setCustomName(e.target.value)}
                          className="w-full h-8 px-2 rounded-lg border border-[#e5dfd3] font-body text-xs text-[#46371c]"
                        />
                      </div>
                      <div>
                        <label className="font-body text-[10px] text-[#75664d] mb-0.5 block">Precio (COP) *</label>
                        <input
                          type="number"
                          value={customPrice}
                          onChange={e => setCustomPrice(Number(e.target.value))}
                          className="w-full h-8 px-2 rounded-lg border border-[#e5dfd3] font-body text-xs text-[#46371c]"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="font-body text-[10px] text-[#75664d] mb-0.5 block">Tamaño</label>
                        <Select
                          value={customSize}
                          onValueChange={setCustomSize}
                        >
                          <SelectTrigger className="w-full h-8 rounded-lg border-[#e5dfd3] text-xs bg-white">
                            <SelectValue placeholder="Seleccionar..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="12 a 15 porciones">12 a 15 porciones</SelectItem>
                            <SelectItem value="20 a 25 porciones">20 a 25 porciones</SelectItem>
                            <SelectItem value="30 a 35 porciones">30 a 35 porciones</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="font-body text-[10px] text-[#75664d] mb-0.5 block">Sabor</label>
                        <input
                          value={customFlavor}
                          onChange={e => setCustomFlavor(e.target.value)}
                          className="w-full h-8 px-2 rounded-lg border border-[#e5dfd3] font-body text-xs text-[#46371c]"
                        />
                      </div>
                      <div>
                        <label className="font-body text-[10px] text-[#75664d] mb-0.5 block">Relleno</label>
                        <input
                          value={customFilling}
                          onChange={e => setCustomFilling(e.target.value)}
                          className="w-full h-8 px-2 rounded-lg border border-[#e5dfd3] font-body text-xs text-[#46371c]"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="font-body text-[10px] text-[#75664d] mb-0.5 block">Cantidad</label>
                        <Select
                          value={String(selectedQuantity)}
                          onValueChange={v => setSelectedQuantity(Number(v))}
                        >
                          <SelectTrigger className="w-full h-8 rounded-lg border-[#e5dfd3] text-xs bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(q => (
                              <SelectItem key={q} value={String(q)}>{q}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="font-body text-[10px] text-[#75664d] mb-0.5 block font-medium">Especificaciones *</label>
                        <input
                          value={customObservations}
                          onChange={e => setCustomObservations(e.target.value)}
                          placeholder="Colores, dedicatoria..."
                          className="w-full h-8 px-2 rounded-lg border border-[#e5dfd3] font-body text-xs text-[#46371c]"
                        />
                      </div>
                    </div>

                    {/* Reference Image Upload */}
                    <div>
                      <label className="font-body text-[10px] text-[#75664d] mb-0.5 block">Imagen de Referencia</label>
                      <div className="flex gap-3 items-center">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleCustomImageUpload}
                          disabled={uploadingImage}
                          className="font-body text-xs text-[#75664d] file:mr-2 file:py-1 file:px-2.5 file:rounded-full file:border-0 file:text-[10px] file:font-semibold file:bg-[#46371c] file:text-[#fcfbf9] file:cursor-pointer hover:file:opacity-90 disabled:opacity-50"
                        />
                        {uploadingImage && <div className="w-4 h-4 border-2 border-[#a0d5ba] border-t-transparent rounded-full animate-spin shrink-0" />}
                      </div>
                      {customImages.length > 0 && (
                        <div className="flex gap-2 mt-2 flex-wrap">
                          {customImages.map((imgUrl, idx) => (
                            <div key={idx} className="relative inline-block">
                              <img src={imgUrl} alt="Referencia" className="w-16 h-16 object-cover rounded-lg border border-[#e5dfd3]" />
                              <button 
                                type="button" 
                                onClick={() => setCustomImages(prev => prev.filter((_, i) => i !== idx))} 
                                className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center font-heading text-[8px] hover:bg-red-700 shadow"
                              >
                                X
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <Button
                  type="button"
                  onClick={handleAddItem}
                  className="w-full h-8 bg-[#a0d5ba] hover:bg-[#82ba9e] text-[#46371c] rounded-lg font-body text-xs font-semibold"
                >
                  Agregar Ítem al Pedido
                </Button>
              </div>

              <div className="bg-[#eef6f2] border border-[#a0d5ba] rounded-2xl p-4 flex justify-between items-center text-sm font-body">
                <div>
                  <p className="text-xs text-[#75664d]">Total Pedido: {formatPrice(dialogTotalPedido)}</p>
                  <p className="text-xs text-green-700">Abonado: {formatPrice(form.initial_payment || 0)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-[#75664d]">Saldo Restante:</p>
                  <p className="font-bold text-lg text-[#46371c]">{formatPrice(dialogSaldoRestante)}</p>
                </div>
              </div>

            </div>

          </div>

          <div className="flex gap-3 pt-4 border-t border-[#e5dfd3] mt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 rounded-xl border-[#e5dfd3] font-body text-xs sm:text-sm text-[#75664d]"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="flex-1 bg-[#46371c] hover:bg-[#2d2312] text-[#fcfbf9] rounded-xl font-body text-xs sm:text-sm"
            >
              {saving ? 'Guardando...' : (editingOrder ? 'Actualizar Pedido' : 'Crear Pedido')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
